// <price-display> Web Component
//
// Phase 2 of CDN caching refactor (memory/cdn_caching_plan.md §2.1). Wraps
// every visible price on the public pages so currency switching no longer
// requires a page reload — and so Phase 3 (when SSR forces canonical USD)
// can convert client-side without server re-render.
//
// Usage:
//   <price-display source-amount="332.80" source-currency="USD">
//     $332.80
//   </price-display>
//
// SSR fallback (the inner text) is the page's canonical-for-the-user
// formatting. The element only re-renders if the user's preferred currency
// differs from `source-currency`. Crawlers + no-JS see the SSR fallback.
//
// Re-renders on `currency-change` event dispatched by <currency-switcher>.

(function () {
    const RATES_CACHE_KEY = 'fx_rates_v1';
    const RATES_TTL_MS = 10 * 60 * 1000; // 10 minutes (matches /api/rates s-maxage)

    let _ratesInFlight = null;

    function _readRatesCache() {
        try {
            const raw = sessionStorage.getItem(RATES_CACHE_KEY);
            if (!raw) return null;
            const obj = JSON.parse(raw);
            if (!obj || (Date.now() - obj.t) > RATES_TTL_MS) {
                sessionStorage.removeItem(RATES_CACHE_KEY);
                return null;
            }
            return obj.rates; // { base, rates, refreshed_at }
        } catch (e) { return null; }
    }

    function _writeRatesCache(rates) {
        try {
            sessionStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ t: Date.now(), rates: rates }));
        } catch (e) { /* ignore */ }
    }

    // Returns Promise<{rates|null, definitive}>. `definitive=false` on
    // network/server error — callers MUST NOT update UI on those, otherwise
    // a transient error would clear all on-page prices to "—".
    function _fetchRates() {
        const cached = _readRatesCache();
        if (cached) return Promise.resolve({ rates: cached, definitive: true });
        if (_ratesInFlight) return _ratesInFlight;
        _ratesInFlight = fetch('/api/rates', { credentials: 'same-origin' })
            .then(function (resp) {
                if (resp.status === 200) return resp.json().then(function (obj) { return { rates: obj, definitive: true }; });
                return { rates: null, definitive: false };
            })
            .then(function (r) {
                if (r.definitive && r.rates) _writeRatesCache(r.rates);
                return r;
            })
            .catch(function () { return { rates: null, definitive: false }; })
            .then(function (r) { _ratesInFlight = null; return r; });
        return _ratesInFlight;
    }

    // Read user's preferred currency from cookie OR localStorage (cookie is
    // canonical; localStorage is mirrored by setCurrency for fast read).
    // decodeURIComponent wrapped in try/catch to survive malformed values.
    function _preferredCurrency() {
        try {
            const ls = localStorage.getItem('preferred_currency');
            if (ls) return ls;
        } catch (e) {}
        const m = document.cookie.match(/(?:^|;\s*)preferred_currency=([^;]+)/);
        if (!m) return '';
        try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
    }

    // Convert amount FROM → TO using the rates map (base USD).
    // Returns null if the conversion can't be computed (missing rate).
    function _convert(amount, from, to, ratesObj) {
        if (!ratesObj || !ratesObj.rates) return null;
        if (from === to) return amount;
        const r = ratesObj.rates;
        const rFrom = (from === 'USD') ? 1 : r[from];
        const rTo = (to === 'USD') ? 1 : r[to];
        if (!rFrom || !rTo) return null;
        // amount is in `from`; first to USD, then to `to`.
        return amount * (rTo / rFrom);
    }

    // Mirrors formatAmount in internal/handlers/handlers.go:1858 — floor
    // to integer + thousands separators. Currency code is ignored for
    // precision (always 0 decimals on display) to match SSR exactly.
    function _formatAmount(amount) {
        const floored = Math.floor(amount);
        return floored.toLocaleString('en-US'); // en-US uses comma separators
    }

    function _symbolFor(code) {
        const map = window.currencySymbols || {};
        return map[code] || (code + ' ');
    }

    // Discount-rollout helpers — read-only consumers of window.clientDiscount,
    // which is populated by static/js/components/discount-loader.js.
    // Eligibility is opt-in per element via data-rollout-eligible="true"
    // (server-side; only on price elements that should reflect the rollout
    // discount — never on API-deal strikethrough siblings, never on tax /
    // commission breakdown lines).
    function _isRolloutEligible(el) {
        return el.getAttribute('data-rollout-eligible') === 'true';
    }
    function _activeDiscountPct() {
        const cd = window.clientDiscount;
        if (!cd || !cd.in_bucket) return 0;
        const p = typeof cd.pct === 'number' ? cd.pct : parseFloat(cd.pct);
        return (isFinite(p) && p > 0) ? p : 0;
    }

    class PriceDisplay extends HTMLElement {
        connectedCallback() {
            // Split init-once state from per-connect listener attach: if a
            // node is detached then re-attached to the DOM, connectedCallback
            // fires again and listeners must re-register (disconnectedCallback
            // removed them on detach). Round-1 audit MEDIUM: a single
            // _wiredOnce guard would dead-end the reconnected instance.
            if (!this._wiredOnce) {
                this._wiredOnce = true;
                // Attributes are available on the opening tag; safe to read sync.
                this._sourceAmount = parseFloat(this.getAttribute('source-amount') || '0');
                this._sourceCurrency = this.getAttribute('source-currency') || '';
            }
            // PR-perf.2: currency-change + discount-loaded handled at
            // module scope (see below — replaces N per-instance listeners).
            const self = this;
            // Safety: reveal within 1000ms even if rates fetch hangs.
            // Stays as setTimeout — separate concern from parser race
            // (visibility cloak release, not textContent write).
            setTimeout(function () { self.classList.add('pd-ready'); }, 1000);
            // Per-element readyState-aware gate for _maybeReformat.
            // setTimeout(0) (the previous fix) proved insufficient under
            // cache-hit conditions in <user-menu> — the macrotask fired
            // BEFORE the parser appended SSR children, so a textContent
            // write was followed by parser-appended SSR text → doubled
            // output ("$1,600$1,600"). DCL gating is robust: defers
            // until full document parsed (children present), OR runs
            // immediately for dynamic insertions (innerHTML inserts
            // children atomically before custom-element reactions fire).
            // memory/hydration_race_fixes_plan.md §3.E.
            const run = function () { self._maybeReformat(); };
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', run, { once: true });
            } else {
                run();
            }
        }

        // Reconstruct the SSR-equivalent display text from the attrs.
        // Reads `source-amount` + `source-currency` and formats them the
        // same way Go's formatAmount renders (floor + en-US thousands +
        // window.currencySymbols lookup).
        //
        // We deliberately don't capture this.textContent at connect time:
        // custom-element upgrade can fire BEFORE the inner text is fully
        // parsed by the HTML parser, so textContent would be empty and
        // the round-trip restoration would blank the element.
        _ssrEquivalentText() {
            return _symbolFor(this._sourceCurrency) + _formatAmount(this._sourceAmount);
        }

        _maybeReformat() {
            const target = _preferredCurrency();
            // No preference set OR target matches source — render in SSR
            // currency (same amount, same code). Async path below also
            // funnels to _writeFinal so the discount-rollout layout is
            // applied consistently across sync + async branches.
            if (!target || target === this._sourceCurrency) {
                this._writeFinal(this._sourceAmount, this._sourceCurrency);
                return;
            }
            // Source amount is unparseable — nothing to convert.
            if (!isFinite(this._sourceAmount) || this._sourceAmount <= 0) {
                this.classList.add('pd-ready');
                return;
            }
            const self = this;
            _fetchRates().then(function (r) {
                if (!r.definitive) {
                    // Transient — reveal SSR text (degraded fallback).
                    self.classList.add('pd-ready');
                    return;
                }
                // Re-read target — user may have switched again while fetch was pending.
                const targetNow = _preferredCurrency();
                if (!targetNow || targetNow === self._sourceCurrency) {
                    self._writeFinal(self._sourceAmount, self._sourceCurrency);
                    return;
                }
                const converted = _convert(self._sourceAmount, self._sourceCurrency, targetNow, r.rates);
                if (converted == null) {
                    // Missing rate — reveal SSR text (degraded fallback).
                    self.classList.add('pd-ready');
                    return;
                }
                self._writeFinal(converted, targetNow);
            });
        }

        // _writeFinal is the single point of DOM mutation for both the
        // currency-converted same-currency and converted-currency branches,
        // and for the discount-rollout overlay. By funneling through here,
        // re-renders (currency-change OR discount-loaded) always re-derive
        // from the cached _sourceAmount/_sourceCurrency, so the strikethrough
        // injected on a previous run is replaced atomically (innerHTML write)
        // — no double-discount, no stale layout.
        _writeFinal(amount, currency) {
            const pct = _isRolloutEligible(this) ? _activeDiscountPct() : 0;
            if (pct > 0) {
                const discounted = Math.round(amount * (100 - pct)) / 100;
                const orig = _symbolFor(currency) + _formatAmount(amount);
                const disc = _symbolFor(currency) + _formatAmount(discounted);
                // innerHTML write replaces children atomically; parser-vs-
                // write race doesn't apply once we're past DCL (only path
                // that reaches _writeFinal is DCL-gated or event-driven
                // post-DCL).
                this.innerHTML =
                    '<span class="line-through text-gray-400 mr-1" aria-hidden="true">' + orig + '</span>' +
                    '<span class="pd-current">' + disc + '</span>' +
                    '<span class="sr-only">Original price ' + orig + '. Current price ' + disc + '.</span>';
                this.classList.add('pd-ready');
                return;
            }
            // Non-discount path: single textContent write with parser-vs-
            // write race guard (preserved from the original code path).
            // If a previous render injected innerHTML strikethrough (e.g.
            // user was in-bucket then admin flipped rollout to 0 between
            // page loads — won't happen in normal flow), the assignment
            // below replaces ALL child nodes with a single text node.
            const want = _symbolFor(currency) + _formatAmount(amount);
            if (this.textContent && this.textContent !== want) {
                this.textContent = want;
            } else if (!this.textContent) {
                // Empty case: defer to SSR text (parser will fill it).
                // Skip the write per the parser-race fix in the original
                // _maybeReformat; same rationale.
            }
            this.classList.add('pd-ready');
        }

    }

    // PR-perf.2: module-level currency-change + discount-loaded listeners
    // replace per-instance pattern. querySelectorAll('price-display') only
    // returns live elements — destroyed nodes from <room-availability>
    // innerHTML swaps are excluded automatically, eliminating the leak the
    // old disconnectedCallback was guarding against. currency-change is
    // user-initiated (rare); discount-loaded fires once per page-load.
    // Both fire rates make the DOM walk on dispatch cheap relative to the
    // N persistent listeners the per-instance pattern allocated.
    document.addEventListener('currency-change', function () {
        const els = document.querySelectorAll('price-display');
        for (let i = 0; i < els.length; i++) {
            if (els[i]._maybeReformat) els[i]._maybeReformat();
        }
    });
    document.addEventListener('discount-loaded', function () {
        const els = document.querySelectorAll('price-display');
        for (let i = 0; i < els.length; i++) {
            if (els[i]._maybeReformat) els[i]._maybeReformat();
        }
    });

    if (!customElements.get('price-display')) {
        customElements.define('price-display', PriceDisplay);
    }

    // bfcache restore: fire currency-change so ALL price-display instances
    // re-evaluate. Module-level (not per-instance) to avoid ~290 listeners
    // per page firing on every pageshow — mirrors localize-text's pattern.
    // The currency-change event fan-out also reaches any imperative
    // surfaces (qty-option walker, reserve-button summary, etc.) — same
    // approach as the cross-tab storage event in <currency-switcher>.
    window.addEventListener('pageshow', function (e) {
        if (e.persisted) {
            document.dispatchEvent(new CustomEvent('currency-change', { bubbles: true }));
        }
    });

    // Pre-Cloudflare M6: qty-option currency walker.
    //
    // <option> content is text-only per HTML5 — <price-display> inside doesn't
    // render. property-availability-fragment.html emits canonical USD price
    // text in option labels + `data-price-usd` numeric attribute on each option.
    // This walker converts the display text on:
    //   - DCL (initial property page load when SSR'd directly, rare today).
    //   - currency-change event (user clicks currency switcher).
    //   - rooms-loaded event (room-availability fragment inserted dynamically;
    //     options aren't in DOM at DCL on the typical /property/<slug> path).
    //
    // Same-currency return from convert() = SSR text is canonical and
    // already in source currency = leave it alone (no DOM mutation).
    // Helper: format an amount applying the rollout discount when active.
    // Mirrors _writeFinal's discount-applied path so qty options + reserve
    // summary visually agree with the rate-card <price-display> render.
    function _formatWithDiscount(amount, currencyCode) {
        const pct = _activeDiscountPct();
        if (pct > 0) {
            const discounted = Math.round(amount * (100 - pct)) / 100;
            return _symbolFor(currencyCode) + _formatAmount(discounted);
        }
        return _symbolFor(currencyCode) + _formatAmount(amount);
    }
    function _walkQtyOptions() {
        const target = _preferredCurrency();
        const options = document.querySelectorAll('select.room-select option[data-price-usd]');
        if (options.length === 0) return;
        if (!target || target === 'USD') {
            // Source-currency path: no FX conversion needed. Still apply
            // rollout discount when in-bucket so the dropdown agrees with
            // the discounted rate-card price.
            options.forEach(function (opt) {
                const usd = parseFloat(opt.getAttribute('data-price-usd') || '0');
                if (!isFinite(usd) || usd <= 0) return;
                opt.text = opt.value + ' (' + _formatWithDiscount(usd, 'USD') + ')';
            });
            return;
        }
        // Use a single rates fetch for the batch — _fetchRates coalesces.
        _fetchRates().then(function (r) {
            if (!r.definitive) return; // transient — leave SSR text
            options.forEach(function (opt) {
                const usd = parseFloat(opt.getAttribute('data-price-usd') || '0');
                if (!isFinite(usd) || usd <= 0) return;
                const converted = _convert(usd, 'USD', target, r.rates);
                if (converted == null) return; // missing rate — leave SSR text
                opt.text = opt.value + ' (' + _formatWithDiscount(converted, target) + ')';
            });
        });
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', _walkQtyOptions);
    } else {
        _walkQtyOptions();
    }
    document.addEventListener('currency-change', _walkQtyOptions);
    document.addEventListener('rooms-loaded', _walkQtyOptions);
    // Re-run on discount-loaded so qty options pick up the rollout
    // discount as soon as /api/discount resolves (typical timing: DCL +
    // ~100ms later). Also re-runs if window.clientDiscount changes later
    // via a tab focus / settings flip.
    document.addEventListener('discount-loaded', _walkQtyOptions);

    // Public helper API for callers that render prices imperatively in JS
    // (e.g. property.js's reserve button summary + room modal) and so can't
    // use the SSR-driven <price-display> element. Same conversion pipeline.
    //
    //   window.PriceDisplay.format(amount, sourceCurrency, opts?)
    //     → Promise<{ text, currency }>
    //
    // `text` is the formatted string with the currency symbol prefix, ready
    // to insert into the DOM. `currency` is the target currency code that
    // was actually rendered (matches user pref, OR sourceCurrency on
    // fallback paths). Callers re-call on every 'currency-change' event.
    window.PriceDisplay = {
        // Numeric conversion API. Returns Promise<{amount, currency}>.
        // Callers that need to BUILD a URL with a numeric amount (not a
        // formatted string) — e.g. navigateToRequestCheckout — must use this
        // instead of `format`, because parsing the formatted text would
        // re-floor + strip the symbol and lose precision.
        //
        // Failure semantics:
        //   - Rates fetch transient failure (5xx) OR missing target rate:
        //     returns {amount: sourceAmount, currency: sourceCurrency}.
        //     Caller decides: checkout flow accepts USD fallback; qty-option
        //     walker no-ops on same-currency return (don't rewrite SSR text).
        convert: function (amount, sourceCurrency) {
            const target = _preferredCurrency() || sourceCurrency;
            if (!target || target === sourceCurrency || !isFinite(amount)) {
                return Promise.resolve({ amount: amount, currency: sourceCurrency });
            }
            return _fetchRates().then(function (r) {
                if (!r.definitive) {
                    return { amount: amount, currency: sourceCurrency };
                }
                const converted = _convert(amount, sourceCurrency, target, r.rates);
                if (converted == null) {
                    return { amount: amount, currency: sourceCurrency };
                }
                return { amount: converted, currency: target };
            });
        },
        // format applies BOTH the FX conversion AND the rollout discount
        // when the visitor is in-bucket (window.clientDiscount). Imperative
        // consumers (property.js reserve summary, room modal) call this and
        // get a single string suitable for direct DOM insertion. Re-call on
        // 'currency-change' AND 'discount-loaded' to refresh.
        format: function (amount, sourceCurrency, opts) {
            opts = opts || {};
            const target = _preferredCurrency() || sourceCurrency;
            // No conversion needed: format in source currency directly.
            if (!target || target === sourceCurrency || !isFinite(amount)) {
                return Promise.resolve({
                    text: _formatWithDiscount(amount, sourceCurrency),
                    currency: sourceCurrency,
                });
            }
            return _fetchRates().then(function (r) {
                if (!r.definitive) {
                    return {
                        text: _formatWithDiscount(amount, sourceCurrency),
                        currency: sourceCurrency,
                    };
                }
                const converted = _convert(amount, sourceCurrency, target, r.rates);
                if (converted == null) {
                    return {
                        text: _formatWithDiscount(amount, sourceCurrency),
                        currency: sourceCurrency,
                    };
                }
                return {
                    text: _formatWithDiscount(converted, target),
                    currency: target,
                };
            });
        },
    };
})();
